import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { NO_PROGRAMM_ID } from "features/programmesActions/Programmes";
import { RootState } from "model";

export type QuickAssessmentResult = {
  guid: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: { [key: string]: any };
};

export default class QuickAssessmentAPI {
  async getIds(): Promise<Array<QuickAssessmentResult>> {
    const res = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/v1/quickAssessment`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    const resultJson = await res.json();
    return resultJson as QuickAssessmentResult[];
  }
}

const quickAssessmentApi = new QuickAssessmentAPI();

export const fetchAllQuickAssessmentResults = createAsyncThunk(
  "quickAssessmentApi/fetchAll",
  async () => {
    const quickAssessmentResults = await quickAssessmentApi.getIds();
    return quickAssessmentResults;
  }
);

const quickAssessmentAdapter = createEntityAdapter<QuickAssessmentResult>({
  selectId: (result) => result.guid,
});

export const {
  selectAll: selectAllQuickAssessmentResults,
  selectById: selectQuickAssessmentResultsById,
  selectEntities: selectQuickAssessmentResultsEntities,
  selectIds: selectQuickAssessmentResultsIds,
} = quickAssessmentAdapter.getSelectors(
  (state: RootState) => state.quickAssessmentResults
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type quickAssessmentResultsState = EntityState<QuickAssessmentResult> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialQuickAssessmentResultsState: quickAssessmentResultsState =
  quickAssessmentAdapter.getInitialState({
    loading: "pending",
    error: null,
    status: null,
  });

const quickAssessmentResultsSlice = createSlice({
  name: "quickAssessmentResults",
  initialState: initialQuickAssessmentResultsState,
  reducers: {
    resetQuickAssessmentResults: () => initialQuickAssessmentResultsState,
  },
  extraReducers: {
    [fetchAllQuickAssessmentResults.fulfilled.type]: (state, { payload }) => {
      quickAssessmentAdapter.upsertOne(state, {
        guid: NO_PROGRAMM_ID,
        results: payload.results,
      });
      state.loading = "idle";
    },
    [fetchAllQuickAssessmentResults.pending.type]: (state, action) => {
      state.loading = "pending";
    },
    [fetchAllQuickAssessmentResults.rejected.type]: (state, action) => {
      state.loading = "idle";
      state.error = action.error;
    },
  },
});

const { actions, reducer } = quickAssessmentResultsSlice;

export const { resetQuickAssessmentResults } = actions;
export const quickAssessmentResultsReducer = reducer;
